import { AHText, Box, OrgIcon } from "arkh";
import React, { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { AHImageStackNew } from "../../../RoutineOccasion/TaskExecution/AHImageStack";
import { TeamScheduleRoutineListItemContent_Routine$key } from "./__generated__/TeamScheduleRoutineListItemContent_Routine.graphql";
import { Platform, View } from "react-native";
import { RoutineOccasionListItemAssignedEmployees } from "./RoutineOccasionListItemAssignedEmployees";
import { TeamScheduleRoutineListItemContent_RoutineOccasion$key } from "./__generated__/TeamScheduleRoutineListItemContent_RoutineOccasion.graphql";

export function TeamScheduleRoutineListItemContent(props: {
  routine: TeamScheduleRoutineListItemContent_Routine$key;
  occasion: TeamScheduleRoutineListItemContent_RoutineOccasion$key | null;
  top?: React.ReactNode;
  isRenderedFromOrg?: boolean;
}) {
  const routineOccasion = useFragment(
    graphql`
      fragment TeamScheduleRoutineListItemContent_RoutineOccasion on RoutineOccasion {
        assigned {
          employee {
            id
          }
        }
        assignedRoles {
          role {
            id
          }
        }
        ...RoutineOccasionListItemAssignedEmployees_RoutineOccasion
      }
    `,
    props.occasion
  );

  const assignedEmployees = useMemo(() => {
    return routineOccasion?.assigned.flatMap((e) =>
      e.employee ? [e.employee] : []
    );
  }, [routineOccasion]);

  const routine = useFragment(
    graphql`
      fragment TeamScheduleRoutineListItemContent_Routine on Routine {
        id
        title
        isOrg
        description
        orgIconUri
        isArchived
        ...RoutineOccasionScreenRoutineDescription_Routine
        descriptionImages {
          ...AHImageStackComponent_Images
        }
      }
    `,
    props.routine
  );

  if (!routine) return null;

  const hasSubtitle =
    (routine.descriptionImages?.length ?? 0) > 0 ||
    (routine.description?.length ?? 0) > 0;

  const hasCompactSubtitle =
    (routine.descriptionImages?.length ?? 0) === 0 && routine.title.length > 30;

  const fontSize = () => {
    if (Platform.OS === "ios") {
      return undefined;
    }

    const extra = !hasSubtitle ? 20 : hasCompactSubtitle ? 10 : 0;
    if (routine.title.length > 50 + extra) {
      return 11;
    }
    if (routine.title.length > 40 + extra) {
      return 12;
    }
    if (routine.title.length > 30 + extra) {
      return 13;
    }
    return undefined;
  };

  return (
    <Box
      style={{
        justifyContent: "center",
      }}
    >
      {props.top}

      <View style={{ flexDirection: "row", alignItems: "center" }}>
        {routine.isOrg && !props.isRenderedFromOrg ? (
          <OrgIcon orgIconUri={routine.orgIconUri} spaceRight="xsmall" />
        ) : null}
        <AHText
          numberOfLines={hasCompactSubtitle ? 2 : 1}
          type="title-small"
          size={fontSize()}
          style={{ alignSelf: "stretch" }}
          spaceRight="small"
          minimumFontScale={0.7}
          adjustsFontSizeToFit={Platform.OS === "ios"}
        >
          {routine.title}
        </AHText>
      </View>
      {hasSubtitle ? (
        <Box
          style={{
            flexDirection: "row",
            alignItems: "center",
          }}
          spaceTop="xsmall"
        >
          {(routine.descriptionImages?.length ?? 0) > 0 ? (
            <Box spaceRight="small">
              <AHImageStackNew images={routine.descriptionImages} />
            </Box>
          ) : null}
          {(routine.description?.length ?? 0) > 0 ? (
            <AHText
              style={{ flex: 1, lineHeight: 15 }}
              numberOfLines={hasCompactSubtitle ? 1 : 2}
            >
              {routine.description?.trim().replace(/\n/g, "")}
            </AHText>
          ) : null}
        </Box>
      ) : null}
      {routineOccasion &&
      ((assignedEmployees?.length ?? 0) > 0 ||
        routineOccasion.assignedRoles.length > 0) ? (
        <Box spaceTop="small">
          <RoutineOccasionListItemAssignedEmployees
            occasion={routineOccasion}
          />
        </Box>
      ) : null}
    </Box>
  );
}
